import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  FieldType,
  FilterListItemChangeArgs,
  FilterListItemModel,
  TsButtonModule,
  TsColorOption,
  TsColorPopoverComponent,
  TsFilterListItemModule,
  TsFlowConnectorComponent,
} from 'tsui';
import { FieldsDialogData } from '../../modals/custom-field-dialog/custom-field-dialog.types';
import { LookupService } from '@services/lookup.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DOCUMENT } from '@angular/common';
import { debounceTime } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConditionalStyling } from '@core/models/conditional-styling';
import { RelatedToType } from '@shared/enums';
import { Store } from '@ngxs/store';
import { ObjectsManagerState } from '../../objects-manager/state/state';

export interface ConditionalFieldStylingFormData {
  conditional_styling: ConditionalStyling[];
  pristine: boolean;
  invalid: boolean;
}

@Component({
  selector: 'conditional-field-styling-form',
  standalone: true,
  imports: [
    TsFlowConnectorComponent,
    ReactiveFormsModule,
    TsFilterListItemModule,
    MatSlideToggleModule,
    TsColorPopoverComponent,
    TsButtonModule,
  ],
  templateUrl: './conditional-field-styling-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConditionalFieldStylingFormComponent implements OnInit {
  @Input({ required: true }) fieldsDialogData: FieldsDialogData;
  @Input({ required: true }) accountColors: TsColorOption[];

  @Output() newStylingConditionAdded = new EventEmitter<void>();
  @Output() conditionalStylingChanged = new EventEmitter<ConditionalFieldStylingFormData>();

  private readonly fb = inject(FormBuilder);
  private readonly docRef = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly allowedObjectTypes: RelatedToType[] = [RelatedToType.Deal, RelatedToType.Property];
  private readonly store = inject(Store);

  protected readonly lookupService = inject(LookupService);
  protected readonly conditionFormattingFormArray: FormArray = new FormArray([]);
  protected readonly fieldFilterData = signal(null);
  protected readonly selectedFilterData = signal([]);

  get controlsMarkedForDestroyCount(): number {
    return this.conditionFormattingFormArray.controls.filter((control) => control.get('_destroy')?.value === true)
      .length;
  }

  get isFormEmpty(): boolean {
    return (
      this.conditionFormattingFormArray.controls.length === 0 ||
      this.controlsMarkedForDestroyCount === this.conditionFormattingFormArray.controls.length
    );
  }

  protected get hasOperators(): boolean {
    return this.fieldsDialogData?.meta?.filtering?.operators?.length > 0;
  }

  protected get fieldLabel(): string {
    return this.fieldsDialogData?.label;
  }

  protected get displayConditionalStyling(): boolean {
    const relatedToType = this.fieldsDialogData?.related_to_type as RelatedToType;

    return (
      this.store.selectSnapshot(ObjectsManagerState.isCustomObject(relatedToType)) ||
      this.allowedObjectTypes.includes(relatedToType as RelatedToType)
    );
  }

  private get conditionalStylingData(): ConditionalStyling[] {
    return this.fieldsDialogData?.meta?.conditional_styling ?? [];
  }

  private get hasConditionalStyling(): boolean {
    return this.conditionalStylingData.length > 0;
  }
  ngOnInit() {
    this.fieldFilterData.set(this.getFieldFilterData());

    this.conditionFormattingFormArray.valueChanges
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (
          change: (Omit<ConditionalStyling, 'color_id'> & {
            color: {
              id: string;
              label: string;
              hex_value: string;
            };
          })[],
        ) => {
          this.conditionalStylingChanged.emit({
            conditional_styling: this.formatFormChange(change),
            pristine: this.conditionFormattingFormArray.pristine,
            invalid: this.conditionFormattingFormArray.invalid,
          });
        },
      );

    if (this.hasConditionalStyling) {
      this.populateFormWithConditionalStylingData(this.conditionalStylingData);
    }
  }

  // this basically is used to sync the filter data with the form because `ts-filter-list-item` doesn't expose
  // form controls for direct usage
  onConditionalStylingFilterChanges({ value, operator }: Partial<FilterListItemChangeArgs>, index: number) {
    if (operator) {
      this.conditionFormattingFormArray.at(index).get('operator').setValue(operator);
      this.conditionFormattingFormArray.at(index).get('value').setValue(null);
    }

    if (value) {
      this.conditionFormattingFormArray.at(index).get('value').setValue(value);
    }

    this.conditionFormattingFormArray.at(index).markAsDirty();
    this.conditionFormattingFormArray.at(index).markAsTouched();

    this.selectedFilterData.update((prev) => {
      const change = {
        fieldName: this.fieldsDialogData?.name || null,
        operator,
        value,
        index,
      };

      return prev.map((item, i) => (i === index ? change : item));
    });
  }

  addConditionalStylingRule() {
    this.conditionFormattingFormArray.push(this.getConditionalStylingForm());
    this.selectedFilterData.update((prev) => [
      ...prev,
      {
        fieldName: this.fieldsDialogData?.name || null,
        operator: null,
        value: null,
        index: this.conditionFormattingFormArray.length - 1,
      },
    ]);

    this.newStylingConditionAdded.emit();
  }

  removeConditionalStylingFilter(index: number) {
    // if id is null, it is a new condition
    const id: any = this.conditionFormattingFormArray.at(index)?.value?.id;

    if (id) {
      this.conditionFormattingFormArray.at(index).get('_destroy').setValue(true);
    } else {
      this.conditionFormattingFormArray.removeAt(index);
      this.selectedFilterData.update((prev) =>
        prev.filter((_, i) => i !== index).map((filter, index) => ({ ...filter, index })),
      );
    }

    // hack: there's an UI glitch on the `ts-list-filter-item` when removing the filter used inside a for loop.
    // some fields are not removed properly and the UI doesn't update properly
    // so we need to trigger a click event on the document to force an update.
    setTimeout(() => {
      this.docRef.dispatchEvent(new Event('click'));
    });
  }

  getConditionRank(index: number) {
    const countOffset = this.controlsMarkedForDestroyCount > 0 ? this.controlsMarkedForDestroyCount : 0;

    return index + 1 - countOffset;
  }

  private getConditionalStylingForm() {
    return this.fb.group({
      operator: [null, Validators.required],
      value: [null, Validators.required],
      color: [null],
      is_bold: [false],
    });
  }

  private getFieldFilterData(): FilterListItemModel[] {
    const [name, meta, label, type] = [
      this.fieldsDialogData?.name,
      this.fieldsDialogData?.meta,
      this.fieldsDialogData?.label,
      this.fieldsDialogData?.type as FieldType,
    ];

    const [filtering, options, multi_select, record_type, scale] = [
      meta?.filtering,
      meta?.options,
      meta?.multi_select,
      meta?.record_type,
      meta?.scale,
    ];

    // TODO: this is a hack to hide the "is" operator for date fields while we implement exact date matching
    const operators =
      type === 'date'
        ? filtering?.operators?.filter((operator) => (operator as any) !== 'is')
        : [...(filtering?.operators ?? [])];

    return [
      {
        name,
        meta: {
          filtering: {
            ...filtering,
            operators,
          },
          options,
          multi_select,
          record_type,
          scale,
        },
        label,
        type,
      },
    ];
  }

  private formatFormChange(
    changes: (Omit<ConditionalStyling, 'color_id'> & {
      color: {
        id: string;
        label: string;
        hex_value: string;
      };
    })[],
  ): ConditionalStyling[] {
    return changes.map((c) => {
      const { id, operator, value, is_bold, _destroy } = c;
      const color_id = c.color?.id ? Number(c.color?.id) : null;

      if (isNaN(color_id)) {
        throw new Error(`Invalid color id: "${c.color.id}" is not a number`);
      }

      return {
        ...(id !== null ? { id } : {}),
        operator,
        value,
        is_bold,
        color_id,
        ...(id && _destroy !== null ? { _destroy } : {}),
      };
    });
  }

  private populateFormWithConditionalStylingData(conditionalStylingData: ConditionalStyling[] = []) {
    conditionalStylingData.forEach(({ id, operator, value, color_id, is_bold }, index) => {
      const color = this.accountColors.find((c) => c.id === color_id);

      this.conditionFormattingFormArray.push(
        this.fb.group({
          id: [id ?? null],
          operator: [operator, Validators.required],
          value: [value, Validators.required],
          color: [color ?? null],
          is_bold: [is_bold],
          _destroy: [null],
        }),
        { emitEvent: false },
      );

      this.selectedFilterData.update((prev) => {
        const change = {
          fieldName: this.fieldsDialogData?.name || null,
          operator,
          value,
          index,
        };

        prev.push(change);

        return prev;
      });
    });
  }
}
